// ===========================================
// LOGIN PAGE - Production Ready HD Design
// Matches Tripnovac AI UI Pattern with Purple/Lilac Theme
// ===========================================

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { authService } from '../../services/auth.service';
import { sanitizeHubReturnPath } from '../../lib/hubReturnPath';
import TurnstileField, { turnstileEnabled } from '../../components/auth/TurnstileField';
import { Mail, Lock, Eye, EyeOff, Globe, Sparkles, Diamond, CheckCircle, Shield } from 'lucide-react';

// HD Travel Images - Crystal Clear Background Images that rotate
const heroImages = [
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1920&q=95&auto=format&fit=crop', // Swiss Alps
  'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=1920&q=95&auto=format&fit=crop', // Kyoto Japan
  'https://images.unsplash.com/photo-1518548419970-58e3b4079ab2?w=1920&q=95&auto=format&fit=crop', // Santorini
  'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=1920&q=95&auto=format&fit=crop', // Paris Eiffel
  'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=1920&q=95&auto=format&fit=crop', // Rome Colosseum
  'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=95&auto=format&fit=crop', // Dubai Skyline
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&q=95&auto=format&fit=crop', // Maldives
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=1920&q=95&auto=format&fit=crop', // Lake Mountains
];

// Hero text content that matches images
const heroContent = [
  { title: 'Transform Travel', subtitle: 'Into Revenue', tagline: 'Intelligent marketing automation for the modern travel agency' },
  { title: 'Your Marketing', subtitle: 'Supercharged', tagline: 'AI-powered campaigns that convert wanderlust into bookings' },
  { title: 'Connect With', subtitle: 'Travelers', tagline: 'Build lasting relationships through personalized engagement' },
  { title: 'Scale Your', subtitle: 'Success', tagline: 'Enterprise-grade tools designed for travel professionals' },
  { title: 'Drive More', subtitle: 'Bookings', tagline: 'Turn leads into loyal customers with smart automation' },
  { title: 'Elevate Your', subtitle: 'Brand', tagline: 'Stand out in the competitive travel marketplace' },
  { title: 'Maximize Your', subtitle: 'ROI', tagline: 'Data-driven insights that optimize every campaign' },
  { title: 'Grow Your', subtitle: 'Agency', tagline: 'The complete sales and marketing platform for travel' },
];

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [twoFactorRequired, setTwoFactorRequired] = useState(false);
  const [challengeToken, setChallengeToken] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const hubAutoStarted = useRef(false);

  const resolveReturnPath = () => {
    const fromState = (location.state as any)?.from;
    const statePath = fromState
      ? `${fromState.pathname || ''}${fromState.search || ''}`
      : '';
    return sanitizeHubReturnPath(
      searchParams.get('returnPath') || statePath || '/dashboard',
      '/dashboard'
    );
  };

  // Surface Cognito/SSO redirect errors (?error=...)
  useEffect(() => {
    const err = searchParams.get('error');
    const fromSso = searchParams.get('sso') === '1';
    if (err) {
      const code = decodeURIComponent(err);
      const friendly: Record<string, string> = {
        missing_token: 'Hub sign-in was missing a launch token. Use Open with Hub SSO, or sign in with email.',
        invalid_token: 'Hub launch token was invalid or expired. Try Open with Hub SSO again, or use email login.',
        token_replay: 'That Hub launch link was already used. Open Sales again from Platform Hub.',
      };
      setSsoError(friendly[code] || code.replace(/_/g, ' '));
      if (fromSso) clearError();
      else clearError();
    }
  }, [searchParams, clearError]);

  // Protected-route / Hub intent: bounce to Hub login?app=tripnovac-sales&returnPath=…
  useEffect(() => {
    if (isAuthenticated) return;
    if (hubAutoStarted.current) return;
    if (searchParams.get('error')) return; // stay on Sales login after fail-closed SSO

    const wantsHub =
      searchParams.get('sso') === 'hub' ||
      searchParams.get('hub') === '1' ||
      searchParams.get('sso') === 'platform-hub';

    if (!wantsHub) return;
    hubAutoStarted.current = true;
    const returnPath = resolveReturnPath();
    authService.loginWithPlatformHub(returnPath).catch((e) => {
      hubAutoStarted.current = false;
      setSsoError(e instanceof Error ? e.message : 'Could not start Platform Hub SSO');
    });
  }, [isAuthenticated, searchParams, location.state]);

  // Rotate background images
  useEffect(() => {
    // Set random initial image on mount
    setCurrentImageIndex(Math.floor(Math.random() * heroImages.length));
    
    const interval = setInterval(() => {
      setImageLoaded(false);
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 20000); // Change every 20 seconds for stability

    return () => clearInterval(interval);
  }, []);

  // Preload next image
  useEffect(() => {
    const nextIndex = (currentImageIndex + 1) % heroImages.length;
    const img = new Image();
    img.src = heroImages[nextIndex];
  }, [currentImageIndex]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (turnstileEnabled() && !captchaToken) {
      setSsoError('Complete bot protection to sign in.');
      return;
    }

    try {
      const result = await login({ email, password, captchaToken: captchaToken || undefined });
      // Check if 2FA is required
      if (result && (result as any).requiresTwoFactor) {
        setTwoFactorRequired(true);
        setChallengeToken((result as any).challengeToken);
        return;
      }
      navigate('/dashboard');
    } catch (err) {
      // Error handled by store
    }
  };

  const handleTwoFactorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    
    try {
      const result = await authService.verifyTwoFactor(challengeToken, twoFactorCode);
      if (result.success && result.data) {
        // Set auth state manually since we bypassed the store's login
        localStorage.setItem('user', JSON.stringify(result.data.user));
        window.location.href = '/dashboard';
      } else {
        throw new Error(result.error || 'Verification failed');
      }
    } catch (err: any) {
      // Show inline error
      useAuthStore.setState({ error: err.message || 'Invalid 2FA code' });
    }
  };

  const currentContent = heroContent[currentImageIndex];

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* HD Background Image - Crystal Clear, No Overlay */}
      <div className="absolute inset-0">
        <img
          src={heroImages[currentImageIndex]}
          alt="Travel destination"
          className={`w-full h-full object-cover transition-opacity duration-1000 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={() => setImageLoaded(true)}
        />
      </div>

      {/* Top Right Badges */}
      <div className="absolute top-6 right-6 z-20 flex items-center gap-3">
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm font-medium">
          <Globe className="w-4 h-4 text-violet-300" />
          <span>249 countries and territories</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/80 backdrop-blur-md text-white text-sm font-medium">
          <Sparkles className="w-4 h-4" />
          <span>AI-Powered</span>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-medium">
          <Diamond className="w-4 h-4" />
          <span>Premium</span>
        </div>
      </div>

      {/* Top Right Stats */}
      <div className="absolute top-6 right-[420px] z-20 hidden xl:flex items-center gap-8 text-white">
        <div className="text-center">
          <div className="text-2xl font-bold">24/7</div>
          <div className="text-xs text-white/70">Support</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold">100%</div>
          <div className="text-xs text-white/70">Secure</div>
        </div>
      </div>

      {/* Bottom Left Hero Text */}
      <div className="absolute bottom-12 left-12 z-20 max-w-lg hidden lg:block">
        <h1 className="text-5xl font-bold text-white leading-tight mb-2">
          {currentContent.title}
          <br />
          <span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
            {currentContent.subtitle}
          </span>
        </h1>
        <p className="text-white/80 text-lg mt-4">
          {currentContent.tagline}
        </p>
      </div>

      {/* Centered Login Card */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Glass Card - Enhanced with better shadow and animation */}
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl shadow-black/20 p-8 border border-white/50 transform hover:scale-[1.01] transition-transform duration-300">
            {/* Logo */}
            <div className="flex justify-center mb-4">
              <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-lg shadow-violet-500/30 ring-4 ring-violet-100">
                <img 
                  src="/assets/sales-icon.jpeg" 
                  alt="Tripnovac Sales" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Brand Name - Using Tripnovac Font Style */}
            <div className="text-center mb-6">
              <div className="flex justify-center mb-1">
                <img 
                  src="/assets/tripnovac-nameplate.jpeg" 
                  alt="Tripnovac" 
                  className="h-8 w-auto object-contain"
                />
              </div>
              {/* Sales & Marketing in matching gradient colors but different font */}
              <p className="text-sm font-light tracking-wider bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 bg-clip-text text-transparent">
                Sales & Marketing
              </p>
            </div>

            {/* Welcome Text */}
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-800">Welcome Back</h3>
              <p className="text-gray-500 text-sm mt-1">Sign in to your account to continue</p>
            </div>

            {/* Error Alert */}
            {(error || ssoError) && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 text-sm">
                {error || ssoError}
              </div>
            )}

            {/* Login Form */}
            {!twoFactorRequired ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-violet-500 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full pl-12 pr-4 py-3.5 rounded-xl border-2 border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 font-medium"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Password
                  </label>
                  <Link to="/forgot-password" className="text-sm text-violet-600 hover:text-violet-700 font-semibold hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-violet-500 transition-colors">
                    <Lock className="w-5 h-5" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-12 pr-12 py-3.5 rounded-xl border-2 border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 placeholder-gray-400 font-medium"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-violet-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Remember Me Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remember-me"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 text-violet-600 border-2 border-gray-300 rounded focus:ring-violet-500 focus:ring-2 cursor-pointer"
                />
                <label htmlFor="remember-me" className="ml-2.5 text-sm text-gray-600 cursor-pointer select-none hover:text-gray-800">
                  Remember me for 30 days
                </label>
              </div>

              <TurnstileField onToken={setCaptchaToken} />

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 hover:from-violet-700 hover:via-purple-700 hover:to-fuchsia-700 text-white font-bold text-lg shadow-lg shadow-violet-500/30 hover:shadow-xl hover:shadow-violet-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Signing In...</span>
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            ) : (
            <form onSubmit={handleTwoFactorSubmit} className="space-y-5">
              <div className="text-center mb-4">
                <Shield className="w-12 h-12 text-violet-500 mx-auto mb-3" />
                <h4 className="text-lg font-bold text-gray-800">Two-Factor Authentication</h4>
                <p className="text-gray-500 text-sm mt-1">Enter the 6-digit code from your authenticator app</p>
              </div>
              <div>
                <input
                  type="text"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-full text-center text-2xl tracking-[0.5em] py-4 rounded-xl border-2 border-gray-200 bg-gray-50/50 focus:bg-white focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 outline-none transition-all text-gray-800 font-mono"
                  maxLength={6}
                  autoFocus
                  required
                />
              </div>
              <button
                type="submit"
                disabled={isLoading || twoFactorCode.length !== 6}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 hover:from-violet-700 hover:via-purple-700 hover:to-fuchsia-700 text-white font-bold text-lg shadow-lg shadow-violet-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify & Sign In'}
              </button>
              <button
                type="button"
                onClick={() => { setTwoFactorRequired(false); setTwoFactorCode(''); setChallengeToken(''); }}
                className="w-full py-2 text-sm text-gray-500 hover:text-violet-600 transition-colors"
              >
                Back to login
              </button>
            </form>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-400">or continue with</span>
              </div>
            </div>

            {/* Platform Hub SSO (Primary) */}
            <button 
              type="button"
              onClick={() => authService.loginWithPlatformHub(resolveReturnPath())}
              className="w-full flex items-center justify-center gap-3 py-4 px-4 rounded-xl border-2 border-violet-500 bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 hover:from-violet-100 hover:via-purple-100 hover:to-fuchsia-100 transition-all text-gray-800 font-bold shadow-lg shadow-violet-500/20 hover:shadow-xl hover:shadow-violet-500/30 transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <Shield className="w-5 h-5 text-violet-600" />
              <span>Tripnovac Platform Hub</span>
              <span className="ml-auto text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white font-bold shadow-sm">SSO</span>
            </button>

            {/* Social Login */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              <button 
                type="button"
                onClick={() => authService.loginWithGoogle()} 
                className="flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-md transition-all text-gray-700 font-medium group"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="group-hover:text-gray-900 transition-colors">Google</span>
              </button>
              <button 
                type="button"
                onClick={() => authService.loginWithApple()}
                className="flex items-center justify-center gap-2.5 py-3.5 px-4 rounded-xl border border-gray-900 bg-black hover:bg-gray-900 transition-all text-white font-medium group"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span>Apple</span>
              </button>
            </div>

            {/* Sign Up Link */}
            <p className="text-center text-gray-600 mt-6">
              Don't have an account?{' '}
              <Link to="/register" className="text-violet-600 hover:text-violet-700 font-semibold">
                Sign up
              </Link>
            </p>

            {/* Security Badges */}
            <div className="flex items-center justify-center gap-6 mt-6 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>SOC 2</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-gray-500">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>GDPR</span>
              </div>
            </div>

            {/* Copyright */}
            <p className="text-center text-xs text-gray-400 mt-4">
              © 2026 Tripnovac by Alpha Libra LLC
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
